/**
 * Custom Height Calculator for Flora Theme
 * Calculates and sets CSS custom properties for announcement bar height
 * Industry-standard approach for precise viewport calculations
 */

(function() {
  'use strict';

  /**
   * Check if we're on mobile viewport (below 750px - Dawn's standard breakpoint)
   */
  function isMobileViewport() {
    return window.innerWidth < 750;
  }

  /**
   * Calculate and set dynamic heights for slideshow components
   * Only runs on desktop viewports (750px and above)
   */
  function setDynamicHeights() {
    // Skip height calculations on mobile - let natural layout flow
    if (isMobileViewport()) {
      console.log('📱 Flora Theme: Mobile viewport detected, skipping height calculations');
      // Reset to default values for mobile
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
      document.documentElement.style.setProperty('--slideshow-controls-height', '46px');
      return;
    }

    console.log('🔍 Flora Theme: Desktop viewport - calculating dynamic heights...');
    
    // Calculate announcement bar height
    const announcementBar = document.querySelector('[id*="announcement-bar"]');
    if (announcementBar) {
      const height = announcementBar.offsetHeight;
      console.log('📏 Flora Theme: Announcement bar found, height:', height + 'px');
      document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
    } else {
      console.log('❌ Flora Theme: No announcement bar found');
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
    }
    
    // Calculate actual header height dynamically (including title wrapping)
    // Try multiple selectors to find the header in Dawn theme
    const headerSelectors = [
      '.shopify-section-group-header-group .header',
      '.shopify-section-header .header', 
      '.header',
      '[id*="header"] .header',
      '.shopify-section-group-header-group'
    ];
    
    let stickyHeader = null;
    for (const selector of headerSelectors) {
      stickyHeader = document.querySelector(selector);
      if (stickyHeader) {
        console.log('📐 Flora Theme: Found header with selector:', selector);
        break;
      }
    }
    
    if (stickyHeader) {
      const headerHeight = stickyHeader.offsetHeight;
      console.log('📐 Flora Theme: Sticky header height (including wrapping):', headerHeight + 'px');
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    } else {
      console.log('❌ Flora Theme: No header found, keeping existing --header-height');
    }
    
    // Calculate slideshow controls height dynamically
    const slideshowControls = document.querySelector('.slideshow__controls');
    if (slideshowControls) {
      const controlsHeight = slideshowControls.offsetHeight;
      console.log('🎮 Flora Theme: Slideshow controls found, height:', controlsHeight + 'px');
      document.documentElement.style.setProperty('--slideshow-controls-height', `${controlsHeight}px`);
    } else {
      console.log('❌ Flora Theme: No slideshow controls found, using default 46px');
      document.documentElement.style.setProperty('--slideshow-controls-height', '46px');
    }
    
    // Log current CSS variable values
    const computedStyle = getComputedStyle(document.documentElement);
    const headerHeight = computedStyle.getPropertyValue('--header-height');
    const announcementHeight = computedStyle.getPropertyValue('--announcement-bar-height');
    const controlsHeight = computedStyle.getPropertyValue('--slideshow-controls-height');
    console.log('🎯 Flora Theme: Current --header-height:', headerHeight);
    console.log('🎯 Flora Theme: Current --announcement-bar-height:', announcementHeight);
    console.log('🎯 Flora Theme: Current --slideshow-controls-height:', controlsHeight);
    
    // Check if slideshow elements exist and log their current heights
    const slideshows = document.querySelectorAll('.slideshow');
    console.log('🖼️ Flora Theme: Found', slideshows.length, 'slideshow elements');
    
    slideshows.forEach((slideshow, index) => {
      const computedSlideshow = getComputedStyle(slideshow);
      const currentHeight = computedSlideshow.minHeight;
      const hasCustomClass = slideshow.classList.contains('banner--small') || 
                            slideshow.classList.contains('banner--medium') || 
                            slideshow.classList.contains('banner--large');
      const hasAdaptClass = slideshow.classList.contains('banner--adapt');
      
      console.log(`📐 Flora Theme: Slideshow ${index + 1}:`, {
        element: slideshow,
        classes: slideshow.className,
        currentMinHeight: currentHeight,
        hasCustomClass: hasCustomClass,
        hasAdaptClass: hasAdaptClass,
        shouldApplyCustomHeight: hasCustomClass && !hasAdaptClass
      });
      
      // Test our CSS selectors manually
      const parentComponent = slideshow.closest('slideshow-component');
      console.log('🔍 Flora Theme: Parent slideshow-component:', parentComponent);
      
      // Try to manually apply the style to see if it works
      // Updated formula: 100vh - announcement bar height - slideshow controls height
      // Header is now positioned dynamically and doesn't take viewport space
      const expectedHeight = `calc(100vh - ${announcementHeight.trim()} - ${controlsHeight.trim()})`;
      console.log('🧮 Flora Theme: Expected height formula:', expectedHeight);

      // Apply style directly for testing
      slideshow.style.minHeight = expectedHeight;
      console.log('🔧 Flora Theme: Applied style directly, new computed height:', getComputedStyle(slideshow).minHeight);
      
      // Check if our CSS rule is being applied by testing CSS selector
      const cssRuleMatches = slideshow.matches('slideshow-component .slideshow.banner--large:not(.banner--adapt)');
      console.log('🎯 Flora Theme: CSS selector matches:', cssRuleMatches);
      
      // Check all applied CSS rules for min-height
      const allStyles = getComputedStyle(slideshow);
      console.log('🔍 Flora Theme: All computed styles - min-height:', allStyles.minHeight);
      console.log('🔍 Flora Theme: All computed styles - height:', allStyles.height);
      
      // Check if our custom CSS is loading
      const customCssLoaded = allStyles.getPropertyValue('--flora-custom-css-loaded');
      console.log('📋 Flora Theme: Custom CSS loaded?', customCssLoaded.trim() === 'true');
    });
  }

  /**
   * Debounce function to limit how often a function can be called
   * @param {Function} func - The function to debounce
   * @param {number} wait - The number of milliseconds to delay
   * @return {Function} - The debounced function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = function() {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Initialize height calculations
   */
  function init() {
    console.log('🚀 Flora Theme: Custom height calculator initializing...');
    
    // Log DOM readiness
    console.log('📄 Flora Theme: Document ready state:', document.readyState);
    
    // Set initial heights
    setDynamicHeights();
    
    // Create debounced resize handler (300ms delay to avoid CPU/GPU overload)
    const debouncedHeightCalculation = debounce(function() {
      console.log('📱 Flora Theme: Window resized (debounced), recalculating heights...');
      setDynamicHeights();
    }, 300);
    
    // Recalculate on window resize with debouncing
    window.addEventListener('resize', debouncedHeightCalculation);
    
    // Recalculate when DOM changes (for dynamic content) - also debounced
    const debouncedMutationHandler = debounce(function() {
      console.log('🔄 Flora Theme: DOM mutation detected (debounced), recalculating heights...');
      setDynamicHeights();
    }, 150);
    
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          debouncedMutationHandler();
        }
      });
    });
    
    // Observe changes to the header group
    const headerGroup = document.querySelector('.shopify-section-group-header-group');
    if (headerGroup) {
      console.log('👀 Flora Theme: Observing header group for changes');
      observer.observe(headerGroup, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    } else {
      console.log('❌ Flora Theme: Header group not found for observation');
    }
    
    console.log('✅ Flora Theme: Custom height calculator initialized');
  }

  // Initialize when DOM is ready
  console.log('🌟 Flora Theme: Custom height calculator script loaded');
  if (document.readyState === 'loading') {
    console.log('⏳ Flora Theme: DOM still loading, waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', init);
  } else {
    console.log('✨ Flora Theme: DOM already ready, initializing immediately...');
    init();
  }

})();